import { INGLIS_BLUE } from '../constants/colors'
import sitesData from '../data/sites.json'
import reuseSitesData from '../data/reuseSites.json'
import { SITE_CATEGORIES, SITE_CATEGORY_LABELS, REUSE_NETWORK } from '../constants/mapConfig'
import PrintQRCode from './PrintQRCode'
import type { ReuseSite, Site, SitesData } from '../types'

const reuseSites = reuseSitesData as ReuseSite[]
const COLUMN_WIDTHS = ['16%', '28%', '9%', '30%', '17%']

function sortByName<T extends { name: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name))
}

function DirectoryHead() {
  return (
    <thead>
      <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1.5px solid #b9c4d3' }}>
        <th scope="col" style={{ padding: '3px 8px 3px 0', fontWeight: 800 }}>Site</th>
        <th scope="col" style={{ padding: '3px 8px', fontWeight: 800 }}>Org &amp; Address</th>
        <th scope="col" style={{ padding: '3px 8px', fontWeight: 800 }}>Region</th>
        <th scope="col" style={{ padding: '3px 8px', fontWeight: 800 }}>What you&apos;ll find</th>
        <th scope="col" style={{ padding: '3px 0 3px 8px', fontWeight: 800 }}>Contact</th>
      </tr>
    </thead>
  )
}

function DirectoryColGroup() {
  return (
    <colgroup>
      {COLUMN_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  )
}

export function SiteRow({ site }: { site: Site }) {
  const contact = site.phone || site.contact || ''
  const website = site.url ? site.url.replace(/^https?:\/\//, '') : ''
  return (
    <tr style={{ borderBottom: '1px solid #eef2f7' }}>
      <td style={{ padding: '5px 8px 5px 0', fontWeight: 600, verticalAlign: 'top' }}>{site.name}</td>
      <td style={{ padding: '5px 8px', color: 'var(--text-secondary)', verticalAlign: 'top' }}>
        {site.org}
        <br />
        <span style={{ color: 'var(--text-faint)' }}>{site.address}</span>
      </td>
      <td style={{ padding: '5px 8px', color: 'var(--text-muted)', verticalAlign: 'top' }}>{site.region}</td>
      <td style={{ padding: '5px 8px', color: 'var(--text-body)', verticalAlign: 'top' }}>{site.audiences.family?.headline ?? ''}</td>
      <td style={{ padding: '5px 0 5px 8px', color: '#004090', verticalAlign: 'top' }}>
        {contact}
        {contact && website && <br />}
        {website && <span style={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>{website}</span>}
      </td>
    </tr>
  )
}

function CategorySection({ category, categorySites }: { category: string; categorySites: Site[] }) {
  return (
    <div style={{ marginBottom: '1.2rem' }}>
      <h2 style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', borderBottom: '1px solid #dbe2ea', paddingBottom: 4, marginBottom: 8 }}>
        {category}
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', tableLayout: 'fixed' }}>
        <DirectoryColGroup />
        <DirectoryHead />
        <tbody>
          {sortByName(categorySites).map(site => <SiteRow key={site.id} site={site} />)}
        </tbody>
      </table>
    </div>
  )
}

function ReuseNetworkSection() {
  const sortedReuseSites = sortByName(reuseSites)

  function subTable(label: string, sites: ReuseSite[]) {
    return (
      <>
        <h3 style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: '0.6rem 0 4px' }}>
          {label} ({sites.length})
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', tableLayout: 'fixed', marginBottom: '0.6rem' }}>
          <DirectoryColGroup />
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1.5px solid #b9c4d3' }}>
              <th scope="col" style={{ padding: '3px 8px 3px 0', fontWeight: 800 }}>Site</th>
              <th scope="col" colSpan={3} style={{ padding: '3px 8px', fontWeight: 800 }}>What you&apos;ll find</th>
              <th scope="col" style={{ padding: '3px 0 3px 8px', fontWeight: 800 }}>Contact</th>
            </tr>
          </thead>
          <tbody>
            {sites.map(site => {
              const website = site.url ? site.url.replace(/^https?:\/\//, '') : ''
              return (
                <tr key={site.name} style={{ borderBottom: '1px solid #f5f6f8' }}>
                  <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, verticalAlign: 'top' }}>{site.name}</td>
                  <td colSpan={3} style={{ padding: '3px 8px', color: 'var(--text-body)', verticalAlign: 'top' }}>{site.description ?? ''}</td>
                  <td style={{ padding: '3px 0 3px 8px', color: '#004090', verticalAlign: 'top' }}>
                    {site.phone || site.email || ''}
                    {(site.phone || site.email) && website && <br />}
                    {website && <span style={{ fontSize: '0.68rem', wordBreak: 'break-all' }}>{website}</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </>
    )
  }

  return (
    <div style={{ marginBottom: '1.2rem' }}>
      <h2 style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', borderBottom: '1px solid #dbe2ea', paddingBottom: 4, marginBottom: 8 }}>
        Statewide Network
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', tableLayout: 'fixed', marginBottom: '0.8rem' }}>
        <DirectoryColGroup />
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1.5px solid #b9c4d3' }}>
            <th scope="col" style={{ padding: '3px 8px 3px 0', fontWeight: 800 }}>Network</th>
            <th scope="col" colSpan={2} style={{ padding: '3px 8px', fontWeight: 800 }}>Details</th>
            <th scope="col" style={{ padding: '3px 8px', fontWeight: 800 }}>Access</th>
            <th scope="col" style={{ padding: '3px 0 3px 8px', fontWeight: 800 }}>Map</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ background: '#f7f9fc' }}>
            <td style={{ padding: '6px 8px 6px 0', fontWeight: 600, verticalAlign: 'top' }}>{REUSE_NETWORK.name}</td>
            <td colSpan={2} style={{ padding: '6px 8px', color: 'var(--text-secondary)', verticalAlign: 'top' }}>
              {reuseSites.length} independently-run equipment-reuse programs across Pennsylvania — hospitals, Lions Clubs, churches, and community groups that redistribute mobility equipment, hearing aids, and other assistive devices, often at low or no cost.
            </td>
            <td style={{ padding: '6px 8px', color: 'var(--text-body)', verticalAlign: 'top' }}>Scan for the live interactive map</td>
            <td style={{ padding: '6px 0 6px 8px', verticalAlign: 'top' }}>
              <PrintQRCode value={REUSE_NETWORK.mapUrl} />
            </td>
          </tr>
        </tbody>
      </table>
      {subTable('Reuse Programs', sortedReuseSites)}
    </div>
  )
}

export default function PrintSiteList() {
  const allSites = (sitesData as SitesData).sites

  return (
    // aria-hidden: this is a print-only view — always mounted (so it's ready the instant the
    // browser print dialog opens) but visually hidden on-screen via sr-only. Without aria-hidden,
    // screen reader users browsing the regular map/list screen would hit a whole second,
    // unrelated document (its own h1 "Site Directory" + region h2s) that isn't part of what
    // they're looking at. Printing itself doesn't consult the accessibility tree, so this has no
    // effect on the printed/print-preview output. role="region" stays on regardless (rather than a
    // roleless div+aria-label) so this is a proper named landmark if the aria-hidden guard is ever
    // lifted later.
    <div className="print-site-list sr-only print:not-sr-only" role="region" aria-label="Site directory for print" aria-hidden="true"
      style={{ fontFamily: "'Public Sans', system-ui, sans-serif", padding: '1cm', color: 'var(--text-strong)' }}>
      <h1 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '0.5rem', color: INGLIS_BLUE }}>
        Inglis Enabled Home Showcase — Site Directory
      </h1>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        inglis.org · Assistive Technology &amp; Accessible Housing across Pennsylvania
      </p>

      {[...SITE_CATEGORIES]
        .sort((a, b) => SITE_CATEGORY_LABELS[a].localeCompare(SITE_CATEGORY_LABELS[b]))
        .map(category => {
          const categorySites = allSites.filter(s => s.category === category)
          return categorySites.length > 0 && (
            <CategorySection key={category} category={SITE_CATEGORY_LABELS[category]} categorySites={categorySites} />
          )
        })}

      <ReuseNetworkSection />
    </div>
  )
}
